// Data-access layer: thin wrappers over the security-definer RPCs (§10).
// All access to profiles/blends goes through these functions.

import { getSupabase } from "./supabase.ts";
import type { DistilledProfile } from "./types.ts";
import type { LbDiaryEntry, LbFavorite } from "./letterboxd.ts";

export interface Profile {
  user_id: string;
  display_name: string;
  share_slug: string;
  profile_data: DistilledProfile;
  letterboxd_username?: string | null;
  summary?: string | null;
  summary_generated_at?: string | null;
  /** True when the owner has hidden their public /:username page. */
  public_hidden?: boolean;
  /** True when the owner opted out of the Letterboxd drift-sync. */
  sync_disabled?: boolean;
  /** Films added by sync since the export baseline. */
  synced_count?: number | null;
  last_synced_at?: string | null;
  /** LLM-personalized taste badges (stickers), cached like the summary. */
  badges?: TasteBadge[] | null;
  updated_at: string;
}

/** One collectible taste-badge sticker. */
export interface TasteBadge {
  emoji: string;
  title: string;
  blurb: string;
}

export interface InviteTeaser {
  display_name: string;
  counts: { watched: number; rated: number; watchlist: number };
}

export interface BlendRow {
  id: string;
  blend_slug: string;
  user_a: string;
  user_b: string;
  recs: Record<string, unknown> | null;
  recs_generated_at: Record<string, string> | null;
  summary: string | null;
  summary_generated_at: string | null;
  created_at: string;
  profile_a: Profile;
  profile_b: Profile;
}

export interface BlendSummary {
  blend_slug: string;
  partner_name: string;
  films_in_common: number;
  /** Placeholder from the list RPC; the precise score renders on the blend page. */
  score?: number;
}

function must() {
  const sb = getSupabase();
  if (!sb) throw new Error("Supabase is not configured.");
  return sb;
}

export async function getInviteTeaser(
  slug: string,
): Promise<InviteTeaser | null> {
  const { data, error } = await must().rpc("get_invite_teaser", { slug });
  if (error) throw error;
  return (data as InviteTeaser) ?? null;
}

export async function createProfile(
  name: string,
  data: DistilledProfile,
): Promise<{ share_slug: string }> {
  const { data: res, error } = await must().rpc("create_profile", {
    name,
    data,
  });
  if (error) throw error;
  return res as { share_slug: string };
}

export async function updateProfile(data: DistilledProfile): Promise<void> {
  const { error } = await must().rpc("update_profile", { data });
  if (error) throw error;
}

export async function deleteProfile(): Promise<void> {
  const { error } = await must().rpc("delete_profile");
  if (error) throw error;
}

/** A point-in-time snapshot of the caller's library (taste over time). */
export interface TasteSnapshot {
  captured_at: string;
  exported_at: string | null;
  watched_count: number;
  rated_count: number;
  watchlist_count: number;
  avg_rating: number | null;
}

/** The caller's library history, oldest → newest, for trend charts. */
export async function getMySnapshots(): Promise<TasteSnapshot[]> {
  const { data, error } = await must().rpc("get_my_snapshots");
  if (error) throw error;
  return (data as TasteSnapshot[]) ?? [];
}

/** A public, read-only taste snapshot served by get_profile_public (no auth). */
export interface PublicProfile {
  display_name: string;
  letterboxd_username: string | null;
  share_slug: string;
  profile_data: DistilledProfile;
  summary: string | null;
  summary_generated_at: string | null;
  badges?: TasteBadge[] | null;
  updated_at: string;
  /** When the RSS drift-sync last ran (null = never). */
  last_synced_at?: string | null;
  /** Films added by sync since the export baseline. */
  synced_count?: number | null;
  public: true;
}

/** Hide/show the caller's public /:username page. */
export async function setPublicHidden(hidden: boolean): Promise<void> {
  const { error } = await must().rpc("set_public_hidden", { hidden });
  if (error) throw error;
}

/** Turn the Letterboxd drift-sync on/off for the caller (privacy opt-out). */
export async function setSyncDisabled(disabled: boolean): Promise<void> {
  const { error } = await must().rpc("set_sync_disabled", { disabled });
  if (error) throw error;
}

/** Public read of a user's taste sheet by Letterboxd username (case-insensitive). */
export async function getProfileByUsername(
  username: string,
): Promise<PublicProfile | null> {
  const { data, error } = await must().rpc("get_profile_public", { username });
  if (error) throw error;
  return (data as PublicProfile) ?? null;
}

/** Live public Letterboxd data (recent watches + four favourites), scraped and
 *  edge-cached by the worker at /api/lb/:username. Best-effort: returns null on
 *  any error (including local dev, where the worker isn't running), so callers
 *  simply omit the enrichment rather than break. */
export interface LbActivity {
  username: string;
  fetchedAt: string;
  recent: LbDiaryEntry[];
  favorites: LbFavorite[];
}

export async function getLetterboxdActivity(
  username: string,
): Promise<LbActivity | null> {
  try {
    const res = await fetch(`/api/lb/${encodeURIComponent(username)}`);
    if (!res.ok) return null;
    if (!res.headers.get("content-type")?.includes("application/json"))
      return null; // SPA fallback in dev
    return (await res.json()) as LbActivity;
  } catch {
    return null;
  }
}

/** Result of a lazy RSS drift-sync. */
export interface SyncResult {
  synced: boolean;
  changed?: boolean;
  /** Films added by THIS sync. */
  added?: number;
  /** Films added by every sync since the last export upload. */
  addedTotal?: number;
  lastSyncedAt?: string;
  reason?: string;
}

/** Drift a stored profile forward from the public Letterboxd diary. Lazy and
 *  fire-and-forget: the function throttles itself server-side (6h), so calling
 *  it on every page view is cheap. Never throws — a failed sync just means the
 *  page shows the last known data. */
export async function syncProfile(
  username: string,
): Promise<SyncResult | null> {
  try {
    const sb = getSupabase();
    if (!sb) return null;
    const { data, error } = await sb.functions.invoke("sync-profile", {
      body: { username },
    });
    if (error) return null;
    return data as SyncResult;
  } catch {
    return null;
  }
}

/** Fetch the current user's own profile, or null if none exists yet. */
export async function getMyProfile(): Promise<Profile | null> {
  const sb = must();
  const { data: userRes } = await sb.auth.getUser();
  const uid = userRes.user?.id;
  if (!uid) return null;
  const { data, error } = await sb
    .from("profiles")
    .select("*")
    .eq("user_id", uid)
    .maybeSingle();
  if (error) throw error;
  return (data as Profile) ?? null;
}

export async function listMyBlends(): Promise<BlendSummary[]> {
  const { data, error } = await must().rpc("list_my_blends");
  if (error) throw error;
  return (data as BlendSummary[]) ?? [];
}

export interface CreateBlendResult {
  blend_slug: string | null;
  self: boolean;
}

export async function createBlend(
  inviteSlug: string,
): Promise<CreateBlendResult> {
  const { data, error } = await must().rpc("create_blend", {
    invite_slug: inviteSlug,
  });
  if (error) throw error;
  return data as CreateBlendResult;
}

export async function getBlend(blendSlug: string): Promise<BlendRow | null> {
  const { data, error } = await must().rpc("get_blend", {
    blend_slug: blendSlug,
  });
  if (error) throw error;
  return (data as BlendRow) ?? null;
}

/** Public, no-auth read of a blend by its (unguessable) slug. */
export async function getBlendPublic(
  blendSlug: string,
): Promise<BlendRow | null> {
  const { data, error } = await must().rpc("get_blend_public", {
    blend_slug: blendSlug,
  });
  if (error) throw error;
  return (data as BlendRow) ?? null;
}

export async function generateRecs(
  blendSlug: string,
  mode: string,
  force = false,
): Promise<unknown> {
  const sb = must();
  const { data: sess } = await sb.auth.getSession();
  const token = sess.session?.access_token;
  const { data, error } = await sb.functions.invoke("recommend", {
    body: { blendSlug, mode, force },
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (error) throw error;
  return data;
}

/** Generate (or fetch cached) the one-line combined-taste blurb for a blend. */
export async function generateBlendSummary(
  blendSlug: string,
  force = false,
): Promise<{ summary: string | null }> {
  const sb = must();
  const { data: sess } = await sb.auth.getSession();
  const token = sess.session?.access_token;
  const { data, error } = await sb.functions.invoke("blend-summary", {
    body: { blendSlug, force },
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (error) throw error;
  return (data as { summary: string | null }) ?? { summary: null };
}

/** A single "feeling lucky" pick from GLM — a film the caller hasn't seen. */
export interface Suggestion {
  title: string;
  year?: number;
  reason: string;
}

/**
 * Ask for one unseen film to watch. Solo (no `blendSlug`) picks for the caller;
 * with `blendSlug` it picks a film NEITHER member has seen that both would love.
 * `exclude` carries picks already shown this session so a re-roll never repeats.
 */
export async function getSuggestion(
  exclude: Suggestion[] = [],
  blendSlug?: string,
): Promise<Suggestion | null> {
  const sb = must();
  const { data: sess } = await sb.auth.getSession();
  const token = sess.session?.access_token;
  const { data, error } = await sb.functions.invoke("suggest", {
    body: {
      exclude: exclude.map((e) => ({ title: e.title, year: e.year })),
      blendSlug,
    },
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (error) throw error;
  return (data as { suggestion: Suggestion | null })?.suggestion ?? null;
}

/** Generate (or fetch cached) the caller's personalized taste badges. */
export async function generateProfileBadges(
  force = false,
): Promise<{ badges: TasteBadge[] | null }> {
  const sb = must();
  const { data: sess } = await sb.auth.getSession();
  const token = sess.session?.access_token;
  const { data, error } = await sb.functions.invoke("profile-badges", {
    body: { force },
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (error) throw error;
  return (data as { badges: TasteBadge[] | null }) ?? { badges: null };
}

/** Generate (or fetch cached) the one-line solo-taste blurb for the caller. */
export async function generateProfileSummary(
  force = false,
): Promise<{ summary: string | null }> {
  const sb = must();
  const { data: sess } = await sb.auth.getSession();
  const token = sess.session?.access_token;
  const { data, error } = await sb.functions.invoke("profile-summary", {
    body: { force },
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
  });
  if (error) throw error;
  return (data as { summary: string | null }) ?? { summary: null };
}
